import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';

interface CollapsibleProps {
  title: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
  headerClassName?: string;
  actions?: React.ReactNode;
  toggleOnHeaderClick?: boolean;
}

export const Collapsible: React.FC<CollapsibleProps> = ({
  title,
  children,
  defaultOpen = false,
  className = '',
  headerClassName = '',
  actions,
  toggleOnHeaderClick = true
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={`rounded-lg ${className}`}>
      <div
        className={`w-full px-4 py-3 text-left font-medium flex items-start justify-between gap-2 ${headerClassName || 'bg-muted hover:bg-muted/90'} text-muted-foreground`}
        onClick={toggleOnHeaderClick ? () => setIsOpen(!isOpen) : undefined}
        role={toggleOnHeaderClick ? 'button' : undefined}
        tabIndex={toggleOnHeaderClick ? 0 : undefined}
        aria-expanded={isOpen}
        onKeyDown={
          toggleOnHeaderClick
            ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(!isOpen);
              }
            }
            : undefined
        }
      >
        <div className="flex-1 text-sm min-w-0 whitespace-normal">{title}</div>
        <div className="ml-2 flex flex-col items-center gap-1 self-start">
          {actions && <div className="flex items-center justify-center">{actions}</div>}
          <button
            type="button"
            className="text-muted-foreground"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
          >
            <ChevronDownIcon
              className={`h-4 w-4 transition-transform duration-200 ${
                isOpen ? 'transform rotate-180' : ''
              }`}
            />
          </button>
        </div>
      </div>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="overflow-hidden border-t border-border bg-card text-card-foreground"
          >
            <div className="px-4 pb-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
